// Corpo do relatório (usado em Resultados e no relatório público /r/:token).
import type { CSSProperties } from 'react'
import { Lightbulb } from 'lucide-react'
import { num } from '@impulso/shared'
import type { ReportDto } from '@impulso/shared/api/reports'
import { Delta, Platform } from '../../ui'
import { Donut, SpendContactsChart } from '../../ui/charts'
import { Kpi, money, money2, roasText, toChartData, vsLabel } from '../insights/components'
import './reports.css'

const PLATFORM_COLORS: Record<string, string> = { meta: '#0866ff', google_ads: '#34a853', tiktok: '#111111', openai_ads: '#0d0d0d', fake: '#9ca3af' }

export function ReportKpis({ report }: { report: ReportDto }) {
  const k = report.kpis
  const vs = vsLabel(report.period.days)
  return (
    <div className="grid g-4 g-kpi">
      <Kpi label="Quanto você investiu" value={money(k.spend)} delta={<Delta value={k.deltas.spend} />} vs={vs} hint="Somando todas as plataformas" />
      <Kpi label="Clientes que te chamaram" value={num(k.results)} delta={<Delta value={k.deltas.results} />} vs={vs} hint="Conversas, ligações e cadastros" />
      <Kpi label="Custo por cliente" value={money2(k.costPerResult)} delta={<Delta value={k.deltas.costPerResult} invert />} vs={vs} hint="Quanto menor, melhor" />
      <Kpi label="Retorno estimado" value={roasText(k.roas)} delta={<Delta value={k.deltas.roas} />} vs={vs} hint={k.estimatedRevenue > 0 ? `Cerca de ${money(k.estimatedRevenue)} em vendas` : 'Informe ticket médio e taxa de venda'} />
    </div>
  )
}

export function ReportSummary({ report }: { report: ReportDto }) {
  return (
    <div className="card">
      <div className="row" style={{ gap: 10, marginBottom: 10 }}>
        <span className="ic purple"><Lightbulb size={18} /></span>
        <h2>Resumo do período</h2>
      </div>
      <div className="stack" style={{ gap: 10, fontSize: 15, lineHeight: 1.65, color: 'var(--text-2)' }}>
        {report.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
      </div>
    </div>
  )
}

export function Funnel({ report }: { report: ReportDto }) {
  const first = report.funnel[0]?.value || 1
  return (
    <div className="card">
      <h2>Do anúncio até a venda</h2>
      <p className="small muted">Quantas pessoas passaram por cada etapa</p>
      <div className="stack mt-16" style={{ gap: 10 }}>
        {report.funnel.map((f) => (
          <div className="funnel-row" key={f.key}>
            <div><div className="strong small">{f.label}</div><div className="small muted">{f.hint}</div></div>
            <div className="funnel-track">
              <div className="funnel-bar" aria-hidden style={{ '--w': Math.max(8, 100 * Math.pow(Math.max(f.value, 0) / first, 0.28)) } as CSSProperties} />
              <span className="funnel-num">{num(f.value)}</span>
            </div>
            <div className="small muted right">{f.rate != null ? `${num(f.rate * 100, 1)}%` : ''}</div>
          </div>
        ))}
      </div>
      {report.funnel.some((f) => f.estimated) && (
        <p className="small muted mt-12">* Vendas estimadas pela taxa de venda que você informou nas configurações do negócio.</p>
      )}
    </div>
  )
}

export function PlatformsCard({ report }: { report: ReportDto }) {
  return (
    <div className="card">
      <h2>Google × Instagram/Facebook</h2>
      <p className="small muted">Onde seu dinheiro rendeu mais</p>
      {report.platforms.length === 0 ? (
        <div className="empty compact small">Sem gasto neste período.</div>
      ) : (
        <div className="row mt-16" style={{ gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Donut
            parts={report.platforms.map((p) => ({ label: p.provider, value: p.results, color: PLATFORM_COLORS[p.provider] ?? '#9ca3af' }))}
            center={<div><div className="strong" style={{ fontSize: 20 }}>{num(report.kpis.results)}</div><div className="small muted">clientes</div></div>}
          />
          <div className="stack" style={{ flex: 1, minWidth: 200, gap: 12 }}>
            {report.platforms.map((p) => (
              <div key={p.provider}>
                <Platform platform={p.provider} />
                <div className="grid g-3 mt-8" style={{ gap: 8 }}>
                  <div><div className="small muted">Investido</div><div className="strong">{money(p.spend)}</div></div>
                  <div><div className="small muted">Clientes</div><div className="strong">{num(p.results)}</div></div>
                  <div><div className="small muted">Custo/cliente</div><div className="strong">{money2(p.costPerResult)}</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export function HeatmapCard({ report }: { report: ReportDto }) {
  const hm = report.heatmap
  const max = Math.max(1, ...hm.values.flat())
  return (
    <div className="card">
      <h2>Quando seus clientes {hm.metric === 'results' ? 'chamam' : 'clicam'}</h2>
      <p className="small muted">{hm.metric === 'results' ? 'Contatos' : 'Cliques'} por dia da semana e horário</p>
      {hm.total === 0 ? (
        <div className="empty compact small">Ainda não há dados por horário.</div>
      ) : (
        <div className="heat mt-16" style={{ gridTemplateColumns: `36px repeat(${hm.slots.length}, minmax(0, 1fr))` }}>
          <span />
          {hm.slots.map((s) => <span key={s} className="small muted" style={{ textAlign: 'center', fontSize: 11 }}>{s}</span>)}
          {hm.days.map((d, di) => (
            <HeatRow key={d} day={d} values={hm.values[di] ?? []} max={max} />
          ))}
        </div>
      )}
    </div>
  )
}

function HeatRow({ day, values, max }: { day: string; values: number[]; max: number }) {
  return (
    <>
      <span className="small muted" style={{ alignSelf: 'center' }}>{day}</span>
      {values.map((v, i) => (
        <span key={i} className="heat-cell" style={{ background: `rgba(91,61,245,${0.08 + (v / max) * 0.82})`, color: v / max > 0.5 ? 'white' : 'var(--text-2)' }}>{num(v)}</span>
      ))}
    </>
  )
}

export function TopAdsCard({ report }: { report: ReportDto }) {
  return (
    <div className="card">
      <h2>Anúncios campeões</h2>
      <p className="small muted">Os que trouxeram clientes pelo menor custo</p>
      {report.topAds.length === 0 ? (
        <div className="empty compact small">Nenhum anúncio trouxe clientes neste período ainda.</div>
      ) : (
        <div className="list mt-16">
          {report.topAds.map((a, i) => (
            <div className="list-item" key={a.id}>
              <span className="strong muted" style={{ width: 18 }}>{i + 1}</span>
              <div className="thumb" style={{ width: 48, height: 48, background: a.imageUrl ? `center / cover no-repeat url("${a.imageUrl}")` : 'linear-gradient(135deg, #7c6bff, #5b3df5)' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="strong" style={{ overflowWrap: 'anywhere' }}>{a.headline || a.name}</div>
                <Platform platform={a.provider} label={a.campaignName} />
              </div>
              <div className="right"><div className="strong">{money2(a.costPerResult)}</div><div className="small muted">por cliente</div></div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function SiteContactsCard({ report }: { report: ReportDto }) {
  const site = report.site
  if (!site) return null
  return (
    <div className="card">
      <h2>Contatos pelas suas páginas</h2>
      <p className="small muted">Pessoas que preencheram o formulário de uma landing page criada aqui</p>
      <div className="grid g-3 mt-16" style={{ gap: 8 }}>
        <div><div className="small muted">Contatos</div><div className="strong">{num(site.contacts)}</div></div>
        <div><div className="small muted">Anúncio por contato</div><div className="strong">{money2(site.costPerContact)}</div></div>
      </div>
      <div className="list mt-12">
        {site.topPages.map((p) => (
          <div className="list-item" key={p.title}>
            <div style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{p.title}</div>
            <div className="strong">{num(p.contacts)}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Relatório inteiro, só leitura. */
export function ReportView({ report }: { report: ReportDto }) {
  return (
    <div className="stack" style={{ gap: 20 }}>
      <ReportKpis report={report} />
      <ReportSummary report={report} />
      <div className="grid g-2">
        <Funnel report={report} />
        <PlatformsCard report={report} />
      </div>
      <div className="card">
        <h2>Investimento × clientes por dia</h2>
        <div className="mt-12"><SpendContactsChart data={toChartData(report.daily)} height={200} /></div>
      </div>
      <div className="grid g-2">
        <HeatmapCard report={report} />
        <TopAdsCard report={report} />
      </div>
      <SiteContactsCard report={report} />
    </div>
  )
}
